import { createContext, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { S, SEO } from "@/i18n/strings";
import es from "@/i18n/es.json";
import fr from "@/i18n/fr.json";
import it from "@/i18n/it.json";
import pt from "@/i18n/pt.json";

export const LOCALES = ["en", "ro", "es", "fr", "it", "pt"];
export const MACHINE = { es, fr, it, pt };
const dig = (o, path) => path.reduce((a, k) => (a == null ? a : a[k]), o);
const Ctx = createContext("en");
export const LocaleProvider = Ctx.Provider;
const IDX = { en: 0, ro: 1 };

export function useT() {
  const locale = useContext(Ctx);
  const i = IDX[locale] ?? 0;
  const extra = MACHINE[locale];
  const raw = (key) => (S[key] ? (extra ? extra.S?.[key] ?? S[key][0] : S[key][i]) : key);
  const t = (key, vars) => {
    let s = raw(key);
    if (vars) Object.entries(vars).forEach(([k, v]) => { s = s.replaceAll(`{${k}}`, v); });
    return s;
  };
  const tl = (key) => (S[key] ? raw(key).split("|") : []);
  const px = (pair, ...path) => (extra ? dig(extra, path) ?? pair[0] : pair[i]);
  const pick = (obj) => (obj ? obj[locale] ?? obj.en ?? "" : "");
  const lp = (path = "") => `/${locale}${path}`;
  return { t, tl, pick, lp, locale, i, px, machine: !!extra };
}

export function useSwitchLocale() {
  const { pathname, search, hash } = useLocation();
  const nav = useNavigate();
  return (to) => {
    const parts = pathname.split("/");
    if (LOCALES.includes(parts[1])) parts[1] = to; else parts.splice(1, 0, to);
    localStorage.setItem("aa_locale", to);
    nav(parts.join("/") + search + hash, { replace: true });
  };
}

export function useSeo(key, titleOverride) {
  const { locale, i, lp, px } = useT();
  const { pathname } = useLocation();
  useEffect(() => {
    const [title, desc] = SEO[key] ? px(SEO[key], "SEO", key).split("|") : [titleOverride ? `${titleOverride} | Ayahuasca Angel` : "Ayahuasca Angel", ""];
    document.title = title;
    document.documentElement.lang = locale;
    const set = (sel, attr, val, make) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement(make.tag); Object.entries(make.attrs).forEach(([a, v]) => el.setAttribute(a, v)); document.head.appendChild(el); }
      el.setAttribute(attr, val);
    };
    if (desc) set('meta[name="description"]', "content", desc, { tag: "meta", attrs: { name: "description" } });
    const rest = pathname.split("/").slice(2).join("/");
    const origin = window.location.origin;
    set('link[rel="canonical"]', "href", `${origin}/${locale}/${rest}`, { tag: "link", attrs: { rel: "canonical" } });
    LOCALES.forEach((l) => set(`link[hreflang="${l}"]`, "href", `${origin}/${l}/${rest}`, { tag: "link", attrs: { rel: "alternate", hreflang: l } }));
  }, [key, i, locale, pathname, titleOverride, lp]);
}

export const fmtMoney = (minor, currency, locale) =>
  new Intl.NumberFormat(locale === "en" ? "en-US" : locale, { style: "currency", currency: (currency || "eur").toUpperCase() }).format((minor || 0) / 100);

export const fmtDateTime = (isoStr, tz, locale, opts = { dateStyle: "full", timeStyle: "short" }) => {
  try { return new Intl.DateTimeFormat(locale === "en" ? "en-GB" : locale, { ...opts, timeZone: tz }).format(new Date(isoStr)); }
  catch { return isoStr; }
};

export const userTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
